import React, { useCallback, useEffect, useState, useRef } from 'react';
import { MapPin, Trash2, Eye, EyeOff, Plus, Loader2, Power, Save, X, Sparkles, Check, Inbox } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Keep the manual-entry dropdown aligned with the LLM's canonical keys so
// hand-created markers and AI-created ones use the same icons on the public map.
const CATEGORIES = [
  'shooting', 'firebombing', 'arson', 'murder', 'assault', 'stabbing',
  'kidnapping', 'drug_bust', 'arrest', 'raid', 'robbery', 'vehicle',
  'court', 'prison', 'gangland', 'other',
];
const MELBOURNE = { lat: -37.8136, lng: 144.9631 };

const emptyForm = () => ({
  title: '',
  summary: '',
  category: 'shooting',
  lat: MELBOURNE.lat,
  lng: MELBOURNE.lng,
  story_url: '',
  event_date: '',
  visible: true,
});

const CrimeMapTab = ({ token }) => {
  const [markers, setMarkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [flag, setFlag] = useState(false);
  const [flagBusy, setFlagBusy] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [backfill, setBackfill] = useState({ state: 'idle', processed: 0, total: 0, inserted: 0 });
  const [submissions, setSubmissions] = useState([]);
  const [submissionBusyId, setSubmissionBusyId] = useState(null);
  const pollRef = useRef(null);

  const authHeaders = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [m, f] = await Promise.all([
        fetch(`${API}/api/admin/crime-map/markers`, { headers: authHeaders }).then((r) => r.ok ? r.json() : []),
        fetch(`${API}/api/admin/feature-flags`, { headers: authHeaders }).then((r) => r.ok ? r.json() : {}),
      ]);
      setMarkers(Array.isArray(m) ? m : []);
      setFlag(Boolean(f?.crime_map_enabled));
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const pollBackfill = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/admin/crime-map/backfill-status`, { headers: authHeaders });
      if (!r.ok) return;
      const s = await r.json();
      setBackfill(s);
      if (s.state === 'done' || s.state === 'idle') {
        clearInterval(pollRef.current);
        pollRef.current = null;
        if (s.state === 'done') await loadAll(); // reload markers
      }
    } catch { /* silent */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, loadAll]);

  // On mount, check if a backfill is already running (from a previous session)
  useEffect(() => {
    pollBackfill();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [pollBackfill]);

  const startBackfill = async () => {
    if (!window.confirm(
      'Auto-extract crime map markers from every published article?\n\n' +
      'This uses Claude to read each article and drop pins for any incidents mentioned. ' +
      'Existing AI-generated pins will be regenerated. Manually created markers are untouched.',
    )) return;
    try {
      const res = await fetch(`${API}/api/admin/crime-map/backfill-from-articles`, {
        method: 'POST', headers: authHeaders,
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setBackfill({ state: 'running', processed: 0, total: 0, inserted: 0 });
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(pollBackfill, 1500);
    } catch (e) { setError(e.message); }
  };

  const loadSubmissions = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/admin/crime-map/submissions`, { headers: authHeaders });
      if (!r.ok) return;
      const s = await r.json();
      setSubmissions(s.submissions || []);
    } catch { /* silent */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { loadSubmissions(); }, [loadSubmissions]);

  const approveSubmission = async (sub) => {
    setSubmissionBusyId(sub.id);
    try {
      const r = await fetch(`${API}/api/admin/crime-map/submissions/${sub.id}/approve`, {
        method: 'POST', headers: authHeaders,
      });
      if (!r.ok) throw new Error('Approve failed');
      await loadSubmissions();
      await loadAll();
    } catch (e) { setError(e.message); }
    finally { setSubmissionBusyId(null); }
  };

  const rejectSubmission = async (sub) => {
    if (!window.confirm(`Reject this submission?\n\n"${sub.title}"`)) return;
    setSubmissionBusyId(sub.id);
    try {
      const r = await fetch(`${API}/api/admin/crime-map/submissions/${sub.id}/reject`, {
        method: 'POST', headers: authHeaders,
      });
      if (!r.ok) throw new Error('Reject failed');
      await loadSubmissions();
    } catch (e) { setError(e.message); }
    finally { setSubmissionBusyId(null); }
  };

  const toggleFlag = async () => {
    setFlagBusy(true);
    try {
      const res = await fetch(`${API}/api/admin/feature-flags`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({ key: 'crime_map_enabled', value: !flag }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setFlag(Boolean(data?.crime_map_enabled));
    } catch (e) { setError(e.message); }
    finally { setFlagBusy(false); }
  };

  const submitForm = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...form,
        lat: parseFloat(form.lat),
        lng: parseFloat(form.lng),
        story_url: form.story_url?.trim() || null,
        event_date: form.event_date?.trim() || null,
      };
      const url = editingId
        ? `${API}/api/admin/crime-map/markers/${editingId}`
        : `${API}/api/admin/crime-map/markers`;
      const res = await fetch(url, {
        method: editingId ? 'PUT' : 'POST',
        headers: authHeaders,
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || `HTTP ${res.status}`);
      }
      setForm(emptyForm());
      setEditingId(null);
      await loadAll();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const editMarker = (m) => {
    setForm({
      title: m.title,
      summary: m.summary || '',
      category: m.category || 'Other',
      lat: m.lat,
      lng: m.lng,
      story_url: m.story_url || '',
      event_date: m.event_date || '',
      visible: m.visible !== false,
    });
    setEditingId(m.id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const deleteMarker = async (id) => {
    if (!window.confirm('Permanently delete this marker?')) return;
    try {
      await fetch(`${API}/api/admin/crime-map/markers/${id}`, { method: 'DELETE', headers: authHeaders });
      await loadAll();
    } catch (e) { setError(e.message); }
  };

  return (
    <div className="space-y-5" data-testid="crime-map-tab">
      <div className={`rounded-lg border p-5 ${flag ? 'border-emerald-500/40 bg-emerald-950/15' : 'border-zinc-800 bg-zinc-900/60'}`}>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <MapPin className="h-5 w-5 text-red-500" />
              <h3 className="text-xl font-black text-white">Crime Map</h3>
              <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-black uppercase tracking-widest ${flag ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/40' : 'bg-zinc-800 text-gray-400 border border-zinc-700'}`}>
                {flag ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
                {flag ? 'Live on /ganglandmap' : 'Hidden'}
              </span>
            </div>
            <p className="text-xs text-gray-400 max-w-xl">
              Pins drop on a dark Melbourne map at <code className="text-red-300">/map</code>. Flip the
              kill-switch off any time and the page returns nothing publicly.
            </p>
          </div>
          <button
            type="button"
            onClick={toggleFlag}
            disabled={flagBusy}
            data-testid="crime-map-flag-toggle"
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg font-black text-xs uppercase tracking-widest transition-colors ${
              flag
                ? 'bg-zinc-800 hover:bg-zinc-700 text-white border border-zinc-700'
                : 'bg-red-600 hover:bg-red-500 text-white'
            }`}
          >
            {flagBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Power className="h-4 w-4" />}
            {flag ? 'Disable Map' : 'Enable Map'}
          </button>
        </div>
      </div>

      {/* AI backfill from articles */}
      <div className="rounded-lg border border-yellow-500/40 bg-yellow-950/10 p-5" data-testid="crime-map-backfill">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="h-5 w-5 text-yellow-300" />
              <h3 className="text-lg font-black text-white">Auto-populate from articles</h3>
            </div>
            <p className="text-xs text-gray-400 max-w-xl leading-relaxed">
              Claude reads every published investigation and drops pins for each incident it can
              locate in Victoria. Runs automatically whenever you save an article. Use this button
              to do a one-shot backfill of everything already published.
            </p>
            {backfill.state === 'running' && (
              <div className="mt-3" data-testid="backfill-progress">
                <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-yellow-400 transition-all"
                    style={{ width: `${backfill.total ? Math.round((backfill.processed / backfill.total) * 100) : 5}%` }}
                  />
                </div>
                <p className="mt-1.5 text-[10px] font-black uppercase tracking-widest text-yellow-300/90">
                  Running · {backfill.processed}/{backfill.total || '?'} articles · {backfill.inserted || 0} markers so far
                </p>
              </div>
            )}
            {backfill.state === 'done' && (
              <p className="mt-3 text-[10px] font-black uppercase tracking-widest text-emerald-300" data-testid="backfill-done">
                ✓ Last run: {backfill.processed} articles processed · {backfill.inserted} markers created
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={startBackfill}
            disabled={backfill.state === 'running'}
            data-testid="crime-map-backfill-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg font-black text-xs uppercase tracking-widest bg-yellow-500 hover:bg-yellow-400 disabled:bg-zinc-800 disabled:text-gray-500 text-black transition-colors"
          >
            {backfill.state === 'running' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {backfill.state === 'running' ? 'Running…' : 'Backfill Now'}
          </button>
        </div>
      </div>

      {/* Community submissions moderation queue */}
      {submissions.length > 0 && (
        <div className="rounded-lg border border-purple-500/40 bg-purple-950/10 p-5" data-testid="crime-map-submissions">
          <div className="flex items-center gap-2 mb-3">
            <Inbox className="h-5 w-5 text-purple-300" />
            <h3 className="text-lg font-black text-white">Community submissions</h3>
            <span className="bg-purple-500/20 border border-purple-500/40 text-purple-200 text-[10px] font-black px-2 py-0.5 rounded tabular-nums">
              {submissions.filter((s) => s.status === 'pending').length} pending
            </span>
          </div>
          <div className="space-y-3">
            {submissions.map((s) => (
              <div
                key={s.id}
                data-testid={`submission-${s.id}`}
                className={`rounded border p-3 ${
                  s.status === 'pending'
                    ? 'border-purple-500/30 bg-black/40'
                    : 'border-zinc-800 bg-zinc-950/60 opacity-70'
                }`}
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="text-[10px] font-black uppercase tracking-widest text-purple-300">
                        {s.category}
                      </span>
                      {s.status === 'rejected' && (
                        <span className="text-[9px] font-black uppercase tracking-widest text-red-300 bg-red-500/15 border border-red-500/40 px-1.5 py-0.5 rounded">
                          rejected
                        </span>
                      )}
                      <span className="text-[10px] text-gray-500 font-mono">
                        {s.lat.toFixed(3)}, {s.lng.toFixed(3)}
                      </span>
                      {s.event_date && (
                        <span className="text-[10px] text-gray-500">· {s.event_date}</span>
                      )}
                    </div>
                    <p className="text-sm font-black text-white">{s.title}</p>
                    <p className="text-xs text-gray-300 mt-1 whitespace-pre-wrap">{s.summary}</p>
                    <div className="mt-2 text-[10px] font-mono text-gray-500 flex items-center gap-3 flex-wrap">
                      <span>IP {s.ip}</span>
                      {s.contact_email && <span>· {s.contact_email}</span>}
                      <span>· {new Date(s.created_at).toLocaleString('en-AU', { timeZone: 'Australia/Melbourne' })}</span>
                    </div>
                  </div>
                  {s.status === 'pending' && (
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        onClick={() => approveSubmission(s)}
                        disabled={submissionBusyId === s.id}
                        data-testid={`submission-approve-${s.id}`}
                        className="inline-flex items-center gap-1 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white font-black text-[10px] uppercase tracking-widest px-2.5 py-1.5 rounded"
                      >
                        <Check className="h-3 w-3" /> Approve
                      </button>
                      <button
                        onClick={() => rejectSubmission(s)}
                        disabled={submissionBusyId === s.id}
                        data-testid={`submission-reject-${s.id}`}
                        className="inline-flex items-center gap-1 bg-red-700/40 hover:bg-red-600/60 disabled:opacity-50 text-red-200 font-black text-[10px] uppercase tracking-widest px-2.5 py-1.5 rounded"
                      >
                        <X className="h-3 w-3" /> Reject
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add/Edit form */}
      <form onSubmit={submitForm} className="rounded-lg border border-red-900/30 bg-zinc-900/60 p-5 space-y-3" data-testid="crime-map-marker-form">
        <div className="flex items-center gap-2 mb-1">
          {editingId ? <Save className="h-5 w-5 text-red-500" /> : <Plus className="h-5 w-5 text-red-500" />}
          <h4 className="text-lg font-black uppercase tracking-wider">{editingId ? 'Edit Marker' : 'Add Marker'}</h4>
          {editingId && (
            <button
              type="button"
              onClick={() => { setEditingId(null); setForm(emptyForm()); }}
              className="ml-auto inline-flex items-center gap-1 text-xs text-gray-400 hover:text-white"
            >
              <X className="h-3.5 w-3.5" /> Cancel
            </button>
          )}
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] font-black tracking-widest uppercase text-gray-400">Title</label>
            <input
              type="text"
              required
              maxLength={160}
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              data-testid="crime-map-form-title"
              className="w-full mt-1 bg-zinc-950 border border-zinc-700 focus:border-red-500 rounded px-3 py-2 text-sm text-white outline-none"
            />
          </div>
          <div>
            <label className="text-[10px] font-black tracking-widest uppercase text-gray-400">Category</label>
            <select
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              data-testid="crime-map-form-category"
              className="w-full mt-1 bg-zinc-950 border border-zinc-700 focus:border-red-500 rounded px-3 py-2 text-sm text-white outline-none"
            >
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>

        <div>
          <label className="text-[10px] font-black tracking-widest uppercase text-gray-400">Summary (≤600 chars)</label>
          <textarea
            value={form.summary}
            onChange={(e) => setForm({ ...form, summary: e.target.value })}
            rows={2}
            maxLength={600}
            data-testid="crime-map-form-summary"
            className="w-full mt-1 bg-zinc-950 border border-zinc-700 focus:border-red-500 rounded px-3 py-2 text-sm text-white outline-none resize-y"
          />
        </div>

        <div className="grid sm:grid-cols-3 gap-3">
          <div>
            <label className="text-[10px] font-black tracking-widest uppercase text-gray-400">Latitude</label>
            <input
              type="number" step="0.000001" min={-90} max={90} required
              value={form.lat}
              onChange={(e) => setForm({ ...form, lat: e.target.value })}
              data-testid="crime-map-form-lat"
              className="w-full mt-1 bg-zinc-950 border border-zinc-700 focus:border-red-500 rounded px-3 py-2 text-sm text-white outline-none"
            />
          </div>
          <div>
            <label className="text-[10px] font-black tracking-widest uppercase text-gray-400">Longitude</label>
            <input
              type="number" step="0.000001" min={-180} max={180} required
              value={form.lng}
              onChange={(e) => setForm({ ...form, lng: e.target.value })}
              data-testid="crime-map-form-lng"
              className="w-full mt-1 bg-zinc-950 border border-zinc-700 focus:border-red-500 rounded px-3 py-2 text-sm text-white outline-none"
            />
          </div>
          <div>
            <label className="text-[10px] font-black tracking-widest uppercase text-gray-400">Event Date</label>
            <input
              type="date"
              value={form.event_date}
              onChange={(e) => setForm({ ...form, event_date: e.target.value })}
              data-testid="crime-map-form-date"
              className="w-full mt-1 bg-zinc-950 border border-zinc-700 focus:border-red-500 rounded px-3 py-2 text-sm text-white outline-none"
            />
          </div>
        </div>

        <div className="grid sm:grid-cols-3 gap-3">
          <div className="sm:col-span-2">
            <label className="text-[10px] font-black tracking-widest uppercase text-gray-400">Story URL (optional)</label>
            <input
              type="url"
              value={form.story_url}
              onChange={(e) => setForm({ ...form, story_url: e.target.value })}
              placeholder="https://www.outlawmedia.com.au/stories/..."
              data-testid="crime-map-form-url"
              className="w-full mt-1 bg-zinc-950 border border-zinc-700 focus:border-red-500 rounded px-3 py-2 text-sm text-white outline-none"
            />
          </div>
          <div className="flex items-end">
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.visible}
                onChange={(e) => setForm({ ...form, visible: e.target.checked })}
                data-testid="crime-map-form-visible"
                className="h-4 w-4 accent-red-600"
              />
              <span className="text-xs font-black uppercase tracking-widest text-gray-300">Show on public map</span>
            </label>
          </div>
        </div>

        {error && (
          <div className="bg-red-950/40 border border-red-500/50 text-red-200 px-3 py-2 rounded text-sm">{error}</div>
        )}

        <div className="flex items-center gap-2">
          <button
            type="submit"
            disabled={saving || !form.title.trim()}
            data-testid="crime-map-form-submit"
            className="inline-flex items-center gap-2 px-4 py-2 rounded bg-red-600 hover:bg-red-500 disabled:bg-zinc-800 disabled:text-gray-500 disabled:cursor-not-allowed text-white text-[11px] font-black uppercase tracking-widest"
          >
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            {editingId ? 'Save Changes' : 'Add Marker'}
          </button>
          <p className="text-[10px] text-gray-500">
            Tip: right-click any spot in Google Maps to copy precise lat/lng.
          </p>
        </div>
      </form>

      {/* Marker list */}
      <div className="rounded-lg border border-zinc-800 bg-zinc-900/60 p-5">
        <h4 className="text-lg font-black uppercase tracking-wider mb-3">
          {loading ? 'Loading…' : `${markers.length} Marker${markers.length === 1 ? '' : 's'}`}
        </h4>
        {!loading && markers.length === 0 && (
          <p className="text-sm text-gray-500 text-center py-8">No markers yet. Drop the first one above.</p>
        )}
        <div className="divide-y divide-zinc-800/80">
          {markers.map((m) => (
            <div key={m.id} className="flex items-start gap-3 py-3" data-testid={`crime-map-row-${m.id}`}>
              <span
                className="mt-1 inline-block h-2.5 w-2.5 rounded-full shrink-0"
                style={{ background: '#dc2626', boxShadow: '0 0 6px rgba(220,38,38,0.6)' }}
                aria-hidden="true"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] font-black tracking-widest uppercase text-red-400">{m.category}</span>
                  {!m.visible && (
                    <span className="text-[10px] font-black uppercase tracking-widest text-gray-500 bg-zinc-800 px-1.5 py-0.5 rounded">Hidden</span>
                  )}
                  <span className="text-[10px] text-gray-500">{m.event_date || ''}</span>
                </div>
                <p className="text-sm font-black text-white truncate">{m.title}</p>
                <p className="text-xs text-gray-400 line-clamp-2">{m.summary}</p>
                <p className="text-[10px] text-gray-600 mt-0.5">{m.lat.toFixed(4)}, {m.lng.toFixed(4)}</p>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <button
                  type="button"
                  onClick={() => editMarker(m)}
                  data-testid={`crime-map-edit-${m.id}`}
                  className="px-2.5 py-1.5 rounded bg-zinc-800 hover:bg-zinc-700 text-white text-[10px] font-black uppercase tracking-widest"
                >
                  Edit
                </button>
                <button
                  type="button"
                  onClick={() => deleteMarker(m.id)}
                  data-testid={`crime-map-delete-${m.id}`}
                  className="px-2.5 py-1.5 rounded bg-zinc-900 hover:bg-red-900/40 border border-zinc-800 hover:border-red-500/60 text-gray-400 hover:text-red-300 text-[10px] font-black uppercase tracking-widest"
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CrimeMapTab;
